import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api/api";
import { ArrowLeft, Save, Pencil, X, Download, Upload } from "lucide-react";

export default function ManageInvoice() {
  const { opfId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [savingRow, setSavingRow] = useState(false);

  const [form, setForm] = useState({
    invoiceNo: "",
    invoiceDate: "",
    invoiceValue: "",
    tax: "",
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, [opfId]);

  const fetchInvoices = async () => {
    try {
      const res = await api.get(`/invoice-manage/list/${opfId}`);
      setRows(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const submit = async () => {
    if (!form.invoiceNo || !form.invoiceDate || !form.invoiceValue) {
      alert("Please fill all fields.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("opf_id", opfId);
      formData.append("invoice_no", form.invoiceNo);
      formData.append("invoice_date", form.invoiceDate);
      formData.append("invoice_value", form.invoiceValue);
      formData.append("tax", form.tax || 0);
      if (file) formData.append("attachment", file);

      await api.post("/invoice-manage/add", formData, {
        headers: { "Content-Type": undefined },
      });

      setForm({ invoiceNo: "", invoiceDate: "", invoiceValue: "", tax: "" });
      setFile(null);

      fetchInvoices();
    } catch (err) {
      console.error("Add invoice failed:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to save.");
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (row) => {
    setEditingId(row.invoice_id);
    setEditForm({
      invoiceNo: row.invoice_no || "",
      invoiceDate: row.invoice_date || "",
      invoiceValue: row.invoice_value || "",
      tax: row.tax || "",
    });
    setEditFile(null);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm(null);
    setEditFile(null);
  };

  const saveEditing = async (invoiceId) => {
    if (!editForm.invoiceNo || !editForm.invoiceDate || !editForm.invoiceValue) {
      alert("Please fill all fields.");
      return;
    }

    try {
      setSavingRow(true);

      const formData = new FormData();
      formData.append("invoice_no", editForm.invoiceNo);
      formData.append("invoice_date", editForm.invoiceDate);
      formData.append("invoice_value", editForm.invoiceValue);
      formData.append("tax", editForm.tax || 0);
      if (editFile) formData.append("attachment", editFile);

      await api.put(`/invoice-manage/update/${invoiceId}`, formData, {
        headers: { "Content-Type": undefined },
      });

      setEditingId(null);
      setEditForm(null);
      setEditFile(null);

      fetchInvoices();
    } catch (err) {
      console.error("Update invoice failed:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to update.");
    } finally {
      setSavingRow(false);
    }
  };

  const downloadInvoice = async (invoiceId, fileName) => {
    try {
      const res = await api.get(`/invoice-manage/download/${invoiceId}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      alert("Failed to download attachment.");
    }
  };

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN")}`;

  return (
    <div className="ml-[68px] pt-16 p-6 bg-[#F4F7FB] min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="border rounded-md p-2 hover:bg-gray-100"
          >
            <ArrowLeft size={18} />
          </button>

          <div>
            <h1 className="text-2xl font-semibold">Manage Invoice</h1>
            <p className="text-sm text-gray-500">Deal ID : {opfId}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow border overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left p-3">Invoice No</th>
              <th className="text-left p-3">Invoice Date</th>
              <th className="text-left p-3">Invoice Value</th>
              <th className="text-left p-3">Tax</th>
              <th className="text-left p-3">Attachment</th>
              <th className="text-left p-3">Action</th>
            </tr>
          </thead>

          <tbody>
            {/* Input Row (new entry) */}
            <tr className="border-t">
              <td className="p-2">
                <input
                  type="text"
                  placeholder="Invoice No"
                  value={form.invoiceNo}
                  onChange={(e) =>
                    setForm({ ...form, invoiceNo: e.target.value })
                  }
                  className="border rounded-md px-3 py-2 w-full"
                />
              </td>

              <td className="p-2">
                <input
                  type="date"
                  value={form.invoiceDate}
                  onChange={(e) =>
                    setForm({ ...form, invoiceDate: e.target.value })
                  }
                  className="border rounded-md px-3 py-2 w-full"
                />
              </td>

              <td className="p-2">
                <input
                  type="number"
                  value={form.invoiceValue}
                  onChange={(e) =>
                    setForm({ ...form, invoiceValue: e.target.value })
                  }
                  className="border rounded-md px-3 py-2 w-full"
                />
              </td>

              <td className="p-2">
                <input
                  type="number"
                  value={form.tax}
                  onChange={(e) => setForm({ ...form, tax: e.target.value })}
                  className="border rounded-md px-3 py-2 w-full"
                />
              </td>

              <td className="p-2">
                <label className="flex items-center gap-2 border rounded-md px-3 py-2 w-full cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
                  <Upload size={14} />
                  {file ? file.name : "Choose file"}
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                </label>
              </td>

              <td className="p-2">
                <button
                  onClick={submit}
                  disabled={loading}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                >
                  <Save size={16} />
                  {loading ? "Saving..." : "Submit"}
                </button>
              </td>
            </tr>

            {/* Existing Rows (editable) */}
            {rows.map((row) => {
              const isEditing = editingId === row.invoice_id;

              return (
                <tr key={row.invoice_id} className="border-t">
                  <td className="p-2">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.invoiceNo}
                        onChange={(e) =>
                          setEditForm({ ...editForm, invoiceNo: e.target.value })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      />
                    ) : (
                      <span className="p-1 font-medium">{row.invoice_no}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <input
                        type="date"
                        value={editForm.invoiceDate}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            invoiceDate: e.target.value,
                          })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      />
                    ) : (
                      <span className="p-1">{row.invoice_date}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editForm.invoiceValue}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            invoiceValue: e.target.value,
                          })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      />
                    ) : (
                      <span className="p-1">
                        {formatCurrency(row.invoice_value)}
                      </span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editForm.tax}
                        onChange={(e) =>
                          setEditForm({ ...editForm, tax: e.target.value })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      />
                    ) : (
                      <span className="p-1">{formatCurrency(row.tax)}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <label className="flex items-center gap-2 border rounded-md px-3 py-2 w-full cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
                        <Upload size={14} />
                        {editFile ? editFile.name : "Replace file"}
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => setEditFile(e.target.files[0])}
                        />
                      </label>
                    ) : row.attachment_name ? (
                      <button
                        onClick={() =>
                          downloadInvoice(row.invoice_id, row.attachment_name)
                        }
                        className="flex items-center gap-2 border rounded-md px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm"
                      >
                        <Download size={14} />
                        Download
                      </button>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => saveEditing(row.invoice_id)}
                          disabled={savingRow}
                          className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md text-sm"
                        >
                          <Save size={14} />
                          {savingRow ? "Saving..." : "Save"}
                        </button>

                        <button
                          onClick={cancelEditing}
                          className="flex items-center gap-1 border px-3 py-2 rounded-md text-sm hover:bg-gray-50"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditing(row)}
                        className="flex items-center gap-1 border px-3 py-2 rounded-md text-sm hover:bg-gray-50"
                      >
                        <Pencil size={14} />
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {rows.length === 0 && (
          <div className="py-12 text-center text-sm font-medium text-gray-400 bg-gray-50/50">
            No Invoices Found
          </div>
        )}
      </div>
    </div>
  );
}